/**
 * Gateway connect handshake flow.
 *
 * Implements the full connect.challenge → connect → hello-ok handshake:
 * 1. Wait for connect.challenge event from gateway (contains nonce)
 * 2. Sign the nonce with the device's Ed25519 private key
 * 3. Send connect request with auth token + device identity
 * 4. Await hello-ok response (or NOT_PAIRED error)
 *
 * On success, returns the HelloOkPayload (including device token if issued).
 * On NOT_PAIRED, throws a PairingRequiredError so the caller can show
 * the approval UI.
 */

import type { WSClient } from '../ws/client';
import type { EventFrame } from '../types/protocol';
import type {
  ConnectChallengePayload,
  ConnectParams,
  HelloOkPayload,
} from '../types/protocol';
import type { GatewayConfig } from '../types/gateway';
import { signConnectPayload, generateDeviceKeypair } from './device';
import {
  PROTOCOL_VERSION,
  CLIENT_INFO,
  CLIENT_ROLE,
  CLIENT_SCOPES,
} from '../utils/constants';
import { buildRequest } from '../ws/frames';

// ── Errors ──

/** Thrown when the gateway returns NOT_PAIRED — device needs approval */
export class PairingRequiredError extends Error {
  deviceId: string;
  constructor(deviceId: string) {
    super('Device not paired — approval required');
    this.name = 'PairingRequiredError';
    this.deviceId = deviceId;
  }
}

/** Thrown when the handshake fails for a non-recoverable reason */
export class HandshakeError extends Error {
  code: string | undefined;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'HandshakeError';
    this.code = code;
  }
}

// ── Handshake ──

/**
 * Perform the full connect handshake with a gateway.
 *
 * @param client - Connected WSClient (must be in 'connected' state)
 * @param gateway - Gateway configuration (including device keypair and token)
 * @returns HelloOkPayload on success
 * @throws PairingRequiredError if the device needs approval
 * @throws HandshakeError on other failures
 */
export async function performHandshake(
  client: WSClient,
  gateway: GatewayConfig,
): Promise<{ helloOk: HelloOkPayload; gateway: GatewayConfig }> {
  // Ensure we have a device keypair
  let device = gateway.device;
  if (!device || !device.privateKey || !device.publicKey) {
    device = await generateDeviceKeypair();
    gateway.device = device;
  }

  // Wait for connect.challenge event
  const challenge = await waitForChallenge(client);

  // Determine which token to use
  const token = gateway.deviceToken || gateway.gatewayToken || '';

  // Sign the challenge
  const signedAt = Date.now();
  const { signature } = await signConnectPayload({
    deviceId: device.id,
    privateKeyB64: device.privateKey,
    nonce: challenge.nonce,
    token,
    signedAt,
  });

  // Build connect request
  const connectParams: ConnectParams = {
    minProtocol: PROTOCOL_VERSION,
    maxProtocol: PROTOCOL_VERSION,
    client: { ...CLIENT_INFO },
    role: CLIENT_ROLE,
    scopes: [...CLIENT_SCOPES],
    caps: ['tool-events', 'voice-turns'],
    commands: [],
    permissions: {},
    auth: { token },
    locale: navigator.language || 'en-US',
    userAgent: `voice-app/${CLIENT_INFO.version}`,
    device: {
      id: device.id,
      publicKey: device.publicKey,
      signature,
      signedAt,
      nonce: challenge.nonce,
    },
  };

  // Send connect request and await response
  const frame = buildRequest('connect', connectParams);

  try {
    const payload = await client.sendTracked<ConnectParams, HelloOkPayload>(
      frame,
      15000, // 15s timeout for connect
    );

    if (payload.type !== 'hello-ok') {
      throw new HandshakeError(`Unexpected response type: ${payload.type}`);
    }

    return { helloOk: payload, gateway };
  } catch (err: unknown) {
    // Check for NOT_PAIRED error
    if (isProtocolError(err) && err.code === 'NOT_PAIRED') {
      throw new PairingRequiredError(device.id);
    }
    // Re-throw other errors
    if (err instanceof Error) {
      throw new HandshakeError(err.message, isProtocolError(err) ? err.code : undefined);
    }
    throw new HandshakeError('Unknown handshake error');
  }
}

// ── Helpers ──

/**
 * Wait for a connect.challenge event from the gateway.
 * The gateway sends this immediately after WebSocket open.
 */
function waitForChallenge(
  client: WSClient,
  timeoutMs: number = 10000,
): Promise<ConnectChallengePayload> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      client.off('event', handler);
      reject(new HandshakeError('Timeout waiting for connect.challenge'));
    }, timeoutMs);

    const handler = (frame: EventFrame) => {
      if (frame.event === 'connect.challenge') {
        clearTimeout(timer);
        client.off('event', handler);
        resolve(frame.payload as ConnectChallengePayload);
      }
    };

    client.on('event', handler);
  });
}

/** Type guard for protocol error objects */
function isProtocolError(err: unknown): err is { code: string; message: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as Record<string, unknown>).code === 'string'
  );
}
